<script setup>
// Solar-Angebot-Vergleich navigation bar. Single source of truth for the
// SAV masthead so every page (homepage + Impressum/Datenschutz/AGB/Widerruf
// + future sub-pages) renders the same brand mark, nav links and CTA.
// The chrome wrapper (SolarangebotvergleichChrome.vue) includes this nav
// for legal pages; the homepage Vue file (Pages/solar-angebot-vergleich/
// Home.vue) uses the same component so both paths stay visually identical.
defineProps({
    brandName: { type: String, default: 'Solar-Angebot-Vergleich' },
    showNotice: { type: Boolean, default: true },
});
</script>

<template>
    <div>
        <div v-if="showNotice" class="sav-notice">
            <div class="sav-notice__inner">
                <span class="sav-notice__dot" aria-hidden="true"></span>
                <span>Aktuell: <strong>0 % Mehrwertsteuer</strong> auf Photovoltaik-Anlagen unter 30 kWp (Nullsteuersatz EStG § 12 Abs. 3).</span>
            </div>
        </div>

        <header class="sav-top">
            <div class="sav-top__inner">
                <a class="sav-brand" href="/" :aria-label="brandName">
                    <svg class="sav-brand__sun" viewBox="0 0 40 40" width="40" height="40" aria-hidden="true" style="width:40px;height:40px;flex-shrink:0;">
                        <circle cx="20" cy="20" r="7.5" fill="var(--sav-accent)" />
                        <g stroke="var(--sav-accent-deep)" stroke-width="2.4" stroke-linecap="round">
                            <line x1="20" y1="3" x2="20" y2="8" />
                            <line x1="20" y1="32" x2="20" y2="37" />
                            <line x1="3" y1="20" x2="8" y2="20" />
                            <line x1="32" y1="20" x2="37" y2="20" />
                            <line x1="7.5" y1="7.5" x2="11" y2="11" />
                            <line x1="29" y1="29" x2="32.5" y2="32.5" />
                            <line x1="32.5" y1="7.5" x2="29" y2="11" />
                            <line x1="11" y1="29" x2="7.5" y2="32.5" />
                        </g>
                    </svg>
                    <span class="sav-brand__wordmark" style="white-space:nowrap;">
                        <span class="sav-brand__name">Solar<span class="sav-brand__dash">-</span>Angebot<span class="sav-brand__dash">-</span>Vergleich</span>
                        <span class="sav-brand__tag">Photovoltaik-Angebote ehrlich vergleichen</span>
                    </span>
                </a>
                <nav class="sav-nav" aria-label="Hauptnavigation">
                    <a href="/#sav-vorteile">Vorteile</a>
                    <a href="/#sav-ablauf">Ablauf</a>
                    <a href="/#sav-foerderung">Förderung</a>
                    <a href="/ratgeber">Ratgeber</a>
                    <a href="/#sav-kontakt">Kontakt</a>
                </nav>
                <a href="/#sav-anfrage" class="sav-top__cta">Jetzt vergleichen</a>
            </div>
        </header>
    </div>
</template>

<style scoped>
.sav-notice { background: var(--sav-notice-bg, #0F1A35); color: #fff; font-size: 14px; }
.sav-notice__inner { max-width: 1240px; margin: 0 auto; padding: 10px 24px; display: flex; align-items: center; gap: 10px; }
.sav-notice__dot { width: 8px; height: 8px; border-radius: 50%; background: var(--sav-sun, #FACC15); flex-shrink: 0; box-shadow: 0 0 0 4px rgba(250,204,21,0.30); }
.sav-notice strong { font-weight: 700; color: var(--sav-sun, #FACC15); }

.sav-top { position: sticky; top: 0; z-index: 40; background: #fff; border-bottom: 1px solid var(--sav-line, #E5E0CC); }
.sav-top__inner { max-width: 1240px; margin: 0 auto; padding: 14px 24px; display: grid; grid-template-columns: auto 1fr auto; gap: 28px; align-items: center; }
.sav-brand { display: inline-flex; align-items: center; gap: 14px; text-decoration: none; color: inherit; }
.sav-brand__sun { width: 40px; height: 40px; display: block; flex-shrink: 0; filter: drop-shadow(0 2px 6px rgba(234,179,8,0.35)); }
.sav-brand__wordmark { display: flex; flex-direction: column; line-height: 1.05; white-space: nowrap; }
.sav-brand__name { font-weight: 800; font-size: 19px; color: var(--sav-ink, #0F1A35); letter-spacing: -0.012em; }
.sav-brand__dash { color: var(--sav-accent-deep, #EAB308); font-weight: 800; padding: 0 1px; }
.sav-brand__tag { color: var(--sav-ink-3, #5C6989); font-size: 11.5px; margin-top: 2px; font-weight: 500; }
.sav-nav { display: flex; gap: 28px; justify-self: center; }
.sav-nav a { color: var(--sav-ink-2, #25325C); text-decoration: none; font-weight: 600; font-size: 15px; }
.sav-nav a:hover { color: var(--sav-accent-deep, #EAB308); }
.sav-top__cta { background: var(--sav-accent, #FACC15); color: var(--sav-cta-ink, #0F1A35); text-decoration: none; padding: 12px 22px; border-radius: 999px; font-weight: 800; font-size: 14px; box-shadow: 0 6px 18px -8px rgba(234,179,8,0.55); white-space: nowrap; }
.sav-top__cta:hover { background: var(--sav-accent-deep, #EAB308); }

@media (max-width: 880px) {
    .sav-top__inner { grid-template-columns: auto auto; gap: 12px; }
    .sav-nav { display: none; }
    /* Keep the CTA a single-line pill - the grid squeezes this column,
       and without nowrap + min-content:0 the label hyphenated into a
       4-line blob that rendered as a broken circle. */
    .sav-brand__wordmark { min-width: 0; }
    .sav-brand__name { font-size: 16px; }
    .sav-brand__tag { display: none; }
    .sav-top__cta { padding: 10px 16px; font-size: 13px; }
}

@media (max-width: 400px) {
    .sav-top__inner { padding: 12px 14px; gap: 8px; }
    .sav-brand__name { font-size: 15px; }
    .sav-top__cta { padding: 9px 13px; font-size: 12px; }
}
</style>
